import { useRef, useState, type ReactNode } from 'react';
import { Input } from './input';

export interface CommitFieldProps {
  id?: string;
  value: string;
  /** Returns an error message to keep editing, or null to accept. */
  validate?: (text: string) => string | null;
  onCommit: (text: string) => void;
  numeric?: boolean;
  unit?: ReactNode;
  type?: string;
  inputMode?: 'text' | 'decimal' | 'numeric' | 'email';
  'aria-describedby'?: string | undefined;
  'aria-label'?: string;
  onErrorChange?: (error: string | null) => void;
}

/**
 * A field that commits on Enter or blur and reverts on Escape; it never writes to stores per
 * keystroke (CLAUDE.md §6.8).
 */
export function CommitField({
  value,
  validate,
  onCommit,
  onErrorChange,
  ...props
}: CommitFieldProps) {
  const [text, setText] = useState(value);
  const [invalid, setInvalid] = useState(false);
  // Escape reverts and blurs; that blur must not commit the abandoned draft.
  const reverting = useRef(false);
  // A new value from the store replaces the draft (React's "adjust state on prop change").
  const [shown, setShown] = useState(value);
  if (shown !== value) {
    setShown(value);
    setText(value);
  }

  function commit(): void {
    if (text === value) return;
    const error = validate ? validate(text) : null;
    setInvalid(error !== null);
    onErrorChange?.(error);
    if (error === null) onCommit(text);
  }

  return (
    <Input
      {...props}
      value={text}
      aria-invalid={invalid || undefined}
      onChange={(event) => {
        setText(event.target.value);
      }}
      onBlur={() => {
        if (reverting.current) {
          reverting.current = false;
          return;
        }
        commit();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          commit();
        } else if (event.key === 'Escape') {
          event.stopPropagation();
          reverting.current = true;
          setText(value);
          setInvalid(false);
          onErrorChange?.(null);
          event.currentTarget.blur();
        }
      }}
    />
  );
}
